import React, {useEffect} from "react"
import "./index.scss"
import SimpleBarChart from "../../components/Charts/BarChart/SimpleBarChart"
import ChartContainer from "../../components/Charts/ChartContainer"
import {useManualQuery} from "graphql-hooks"
import {UseStores} from "../../hooks/UseStores"
import {observer} from "mobx-react-lite"
import {BEHAVIORAL_AUDIENCE_AFFINITY} from "../../gql/gqlInsights"
import {insightClient} from "../../gql/gqlInterceptor"
import {brandIdToSicCode, baKeyToText} from "../../utils/utils"
import {Spinner} from "../../components/Spinner"
import InternalError from "../../components/InternalError"
import {chartsPallete} from "../../assets/shared/colorConstants"
import EmptyDataContainer from "../../pages/Insights/EmptyDataContainer"

const BehavioralAudienceAffinity = () => {
  let domChart = null
  let query = BEHAVIORAL_AUDIENCE_AFFINITY
  const label = "Behavioral Audience Affinity"
  const type = "insights_behavioral_audience_affinity"
  let minAffinity = null
  let maxAffinity = null

  const {insightStore, insightsPopupStore} = UseStores()

  const brandId = insightStore.brandId
  const sicCode = brandIdToSicCode(brandId)
  const month = insightStore.insightMonth
  const marketType = insightStore.insightMarketType
  const marketTypeValue = insightStore.insightMarketTypeValue
  const skipCache = insightsPopupStore.skipCacheForBAA
  const csvData = []

  const [fetchData, {loading, error, data}] = useManualQuery(query, {
    variables: {brandId, month, marketType, marketTypeValue},
    client: insightClient,
    skipCache: skipCache,
  })

  useEffect(() => {
    if (month !== "") {
      fetchData()
    }
  }, [month, sicCode, brandId, marketType, marketTypeValue])

  if (loading) {
    domChart = (
      <div align="center" className="spinner-container">
        <Spinner size="medium" />
      </div>
    )
  }

  if (error) {
    insightsPopupStore.setSkipCacheForBAA(true)
    domChart = (
      <div className="error-message-container">
        <InternalError />
      </div>
    )
  }

  if (data && data.brandAffinity) {
    insightsPopupStore.setSkipCacheForBAA(false)

    const behavioralAudiencesAffinity = data.brandAffinity.behavioralAudiencesAffinity

    if (behavioralAudiencesAffinity.length === 0) {
      domChart = <EmptyDataContainer />
    } else {
      const currentBrandBaAudienceAffinity = behavioralAudiencesAffinity[0]
      const baAffinityData = []
      for (const ba of currentBrandBaAudienceAffinity.baMarket[0].behaviouralAudience) {
        // can't use ba.baCode.startsWith("other") check as we are getting few other audience (eg: cost)
        // for which the mapping isn't available even in taxonomy db. still talking with chandan on this one.
        let display_name = baKeyToText(ba.baCode)
        if (!display_name) continue
        baAffinityData.push({
          BehavioralAudience: display_name,
          AffinityIndex: ba.affinityIndex,
        })
        csvData.push({
          "Behavioral Audience Name": display_name,
          "Affinity Index": ba.affinityIndex,
        })
      }
      baAffinityData.sort(function (ba1, ba2) {
        return ba2["AffinityIndex"] - ba1["AffinityIndex"]
      })
      const chartData = baAffinityData.slice(0, 16)
      const affinityIndexList = chartData && chartData.map((ba) => ba["AffinityIndex"])
      if (affinityIndexList.length) {
        minAffinity = Math.min(...affinityIndexList)
        maxAffinity = Math.max(...affinityIndexList)
      }
      domChart = (
        <SimpleBarChart
          chartData={chartData}
          xDataKey="BehavioralAudience"
          yDataKey="AffinityIndex"
          color={chartsPallete[1]}
          hidePercent={true}
          tickCount={8}
          barSize={12}
          chartHeight={258}
          showEllipsis={true}
          marginTop={40}
        />
      )
    }
  }

  return (
    <>
      <ChartContainer
        type={type}
        csvData={csvData}
        label={label}
        hideDownload={true}
        showMinMax={error ? false : true}
        max={maxAffinity}
        min={minAffinity}
      >
        {domChart}
      </ChartContainer>
    </>
  )
}
export default observer(BehavioralAudienceAffinity)
